import hexToHSL from 'hex-to-hsl'
import { env } from '~/service/env'

/**
 * Generate a full color palette of 9 colors given the center color.
 */
export function generatePaletteColors(
  /**
   * The name of the color palette. This will be used to create the variable
   * names associated with each color in the palette.
   */
  paletteName: string,
  /**
   * The color in the center of the palette (.ie the 500 value)
   */
  centerColor: string,
  /**
   * Optional saturation value to use for the center color. If not passed, then
   * the saturation of centerColor is used. If this value is greater than the
   * actual saturation, then the actual saturation is used. This allows us to
   * generate a greyscale color palette while ensuring that we don't
   * accidentally introduce red tones into a grayscale color.
   */
  saturationParam?: number,
  /**
   * Optional lightness value to use for the center color. If not passed, then
   * the lightness of centerColor is used. If this value is less than the
   * actual lightness, then the actual saturation is used. This allows us to
   * generate a greyscale color palette while ensuring that we don't
   * accidentally make the grayscale palette darker than the primary.
   */
  lightnessParam?: number
) {
  const [hue, saturationCalc, lightnessCalc] = hexToHSL(centerColor)
  // Allow passing a saturation so we can use it to generate a desaturated
  // grayscale palette from the primary or secondary colors. However, if our
  // saturation parameter is actually more saturated than the community color
  // (ex. the primary color is a grey), then use the calculated saturation
  // instead of our value. Otherwise, we would tint the primary color with a
  // random hue.
  const saturation =
    saturationParam && saturationParam < saturationCalc
      ? saturationParam
      : saturationCalc
  // Allow passing a lightness so we can use it to generate palette colors.
  // Similar to the saturation param, if the given lightness is actually darker
  // than the lightness of the community color, then use the calculated
  // lightness.
  const lightness =
    lightnessParam && lightnessParam > lightnessCalc
      ? lightnessParam
      : lightnessCalc

  // The range above/below the center color that we can safely use to generate
  // colors. The brighter/darker the center color, the narrower the color range
  // of the palette.
  const range = Math.min(lightness, 100 - lightness)
  // We use a range of 5 but pick the 4 colors above/below the center color so
  // that the first and last colors are not full black/white
  const factor = range / 5
  const start = lightness + factor * 4
  const out = []
  // Generate each color in the palette ranging from 100-900
  for (let i = 1; i < 10; i++) {
    const colorName = i * 100
    const l = start - (i - 1) * factor
    // Ensure that we always use the exact center color value.
    const color =
      colorName === 500 && !saturationParam && !lightnessParam
        ? centerColor
        : `hsl(${hue}, ${saturation}%, ${l.toFixed(2)}%)`
    out.push({
      name: `--light-${paletteName}-${colorName}`,
      color,
    })
  }
  return out
}

/**
 * Set the value of a dynamic CSS size variable (ex. the header height)
 */
export function setDynamicThemeSize(
  /**
   * The name of the CSS size variable being set. If the
   * variable is `--size-header-height`, then use `header-height`.
   */
  name: string,
  /**
   * The variable value to use. It will be converted to pixels.
   */
  value: number
) {
  if (
    !env.test &&
    !getComputedStyle(document.documentElement).getPropertyValue(
      `--size-${name}`
    )
  ) {
    console.warn('Unable to find dynamic size variable', name)
  }

  document.documentElement.style.setProperty(`--size-${name}`, `${value}px`)
}

export type Hex = `#${string}`

/**
 * Define new palettes and override the global theme variables defined on the DocumentElement
 * based on the community's theme colors.
 *
 */
export function setThemeColors(
  /**
   * The primary color hex value.
   */
  primary?: Hex,
  /**
   * The secondary color hex value.
   */
  secondary?: Hex
) {
  if (primary) {
    const primaryColors = generatePaletteColors('primary', primary)
    primaryColors.forEach(({ name, color }) =>
      document.documentElement.style.setProperty(name, color)
    )
    // Use a dark primary color for focus states.
    document.documentElement.style.setProperty(
      `--light-foreground-focus`,
      primaryColors[7].color
    )
    document.documentElement.style.setProperty(
      `--light-background-focus`,
      primaryColors[7].color
    )
    // Use the main primary color as the HTML background.
    document.documentElement.style.setProperty(
      `--light-background-html`,
      primaryColors[4].color
    )

    // Calculate the grayscale using a bit of the primary color
    const grayscaleColors = generatePaletteColors('grayscale', primary, 30, 70)
    grayscaleColors.forEach(({ name, color }) =>
      document.documentElement.style.setProperty(name, color)
    )
    // Use the lightest grayscale color as the page loader spinner color. This
    // should show up nicely over the primary background color.
    document.documentElement.style.setProperty(
      `--light-foreground-loader`,
      grayscaleColors[1].color
    )
  }
  if (secondary) {
    const secondaryColors = generatePaletteColors('secondary', secondary)
    secondaryColors.forEach(({ name, color }) =>
      document.documentElement.style.setProperty(name, color)
    )
  }
}
